import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import TutorStudentsModal from './TutorStudentsModal';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiUser, FiMail, FiPhone, FiUsers, FiEye, FiSearch, FiBookOpen } = FiIcons;

    const TutorManagement = () => {
      const { users, students } = useData();
      const [viewingTutor, setViewingTutor] = useState(null);
      const [searchTerm, setSearchTerm] = useState('');

      // Filter only active tutors
      const tutors = users.filter(user => user.role === 'tutor' && user.status === 'active');

      // Helper to get full name consistently
      const getTutorName = (user) => {
        if (user.name) return user.name;
        return `${user.firstName || ''} ${user.lastName || ''}`.trim();
      };

      // ROBUST MATCHING LOGIC
      const isTutorAssigned = (student, tutor) => {
        const tutorFullName = getTutorName(tutor).toLowerCase();
        const tutorFirstName = (tutor.firstName || '').toLowerCase();
        const tutorLastName = (tutor.lastName || '').toLowerCase();
        
        // Check if a specific tutor string matches this tutor
        const checkMatch = (tutorStr) => {
          if (!tutorStr) return false;
          const str = tutorStr.toLowerCase().trim();
          
          return (
            str === tutorFullName || 
            str === tutorFirstName || 
            (tutorLastName && str.includes(tutorLastName) && str.includes(tutorFirstName)) ||
            (str === 'system administrator' && tutor.role === 'admin') // Handle generic admin case
          );
        };

        // Check new array-based subjects format
        if (student.subjects && Array.isArray(student.subjects)) {
          return student.subjects.some(sub => checkMatch(sub.tutor));
        }
        // Fallback to legacy single tutor format
        return checkMatch(student.tutor);
      };

      // Count students assigned to a specific tutor
      const getStudentCount = (tutor) => {
        return students.filter(student => isTutorAssigned(student, tutor)).length;
      };

      // Filter tutors based on search term
      const filteredTutors = tutors.filter(tutor => {
        const name = getTutorName(tutor).toLowerCase();
        const email = (tutor.email || '').toLowerCase();
        const search = searchTerm.toLowerCase();
        return name.includes(search) || email.includes(search);
      });

      return (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Tutors</h2>
              <p className="text-sm text-gray-600 mt-1">View tutor details, assigned subjects, and students</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="bg-white rounded-xl shadow-sm p-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                placeholder="Search tutors by name or email..." 
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" 
              />
            </div>
          </div>

          {/* Tutors List Table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tutor Profile</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Subjects</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact Info</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Workload</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTutors.length > 0 ? (
                    filteredTutors.map((tutor, index) => {
                      const studentCount = getStudentCount(tutor);
                      const tutorName = getTutorName(tutor);
                      
                      return (
                        <motion.tr 
                          key={tutor.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-lg">
                                {tutor.firstName?.[0]}{tutor.lastName?.[0]}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{tutorName}</div>
                                <div className="text-xs text-gray-500">{tutor.occupation || 'Tutor'}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1">
                              {tutor.assignedSubjects && tutor.assignedSubjects.length > 0 ? (
                                tutor.assignedSubjects.map((sub, i) => (
                                  <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                    {sub}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs text-gray-400 italic">General / All</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 flex items-center gap-2">
                              <SafeIcon icon={FiMail} className="text-gray-400" /> {tutor.email}
                            </div>
                            {tutor.phone && (
                              <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                <SafeIcon icon={FiPhone} className="text-gray-400" /> {tutor.phone}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${studentCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                              <SafeIcon icon={FiUsers} className="mr-1.5" />
                              {studentCount} Student{studentCount !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button 
                              onClick={() => setViewingTutor({ ...tutor, name: tutorName })}
                              className="text-indigo-600 hover:text-indigo-900 flex items-center gap-1 transition-colors"
                            >
                              <SafeIcon icon={FiEye} /> View Students
                            </button>
                          </td>
                        </motion.tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                        <SafeIcon icon={FiUser} className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <p>No active tutors found matching your search.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Student List Modal */}
          {viewingTutor && (
            <TutorStudentsModal 
              tutor={viewingTutor} 
              onClose={() => setViewingTutor(null)} 
            />
          )}
        </div>
      );
    };

    export default TutorManagement;